// Ruteo simple: ordena las paradas de un recorrido para que no salte de un
// lado a otro. Por defecto, distancia en línea recta; con `cost` (la matriz
// de tiempos por calle de Valhalla, ver lib/streetRouting) ordena por tiempo
// real de manejo. Primero el vecino más cercano desde el inicio y después
// mejoras locales (2-opt y mover tramos cortos) hasta que ninguna acorta el
// total. Por calle ir y volver no cuesta lo mismo (manos únicas): las mejoras
// cuentan también lo que cambia al recorrer un tramo al revés.
//
// Inicio y fin son opcionales: sin fin, el recorrido termina donde le quede
// más corto; con fin, la última parada es la que mejor deja para llegar ahí.
// Las paradas sin coordenadas no se pueden ubicar: van al final, en el orden
// en que estaban, y se informan aparte.

export type Pt = { lat: number; lng: number };

const R_KM = 6371;
const rad = (x: number) => (x * Math.PI) / 180;

export function haversineKm(a: Pt, b: Pt): number {
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Una coordenada sirve si es un número finito, ninguna de las dos es 0 (el
// geocodificador devuelve 0 cuando no encuentra) y cae en un recuadro amplio
// de la Argentina: lat y lng invertidas, o un punto en otro país, son datos
// rotos que estirarían el recorrido. Lo que no pasa va «sin coordenadas».
const AR = { latMin: -56, latMax: -21, lngMin: -74, lngMax: -53 };
export const hasCoords = (p: Partial<Pt> | null | undefined): p is Pt =>
  !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && p.lat !== 0 && p.lng !== 0
  && (p.lat as number) >= AR.latMin && (p.lat as number) <= AR.latMax
  && (p.lng as number) >= AR.lngMin && (p.lng as number) <= AR.lngMax;

// Largo total, en km, de recorrer `points` en ese orden (con inicio y fin si hay).
export function pathKm(points: Pt[], start: Pt | null = null, end: Pt | null = null): number {
  const all = [...(start ? [start] : []), ...points, ...(end ? [end] : [])];
  let km = 0;
  for (let i = 1; i < all.length; i++) km += haversineKm(all[i - 1], all[i]);
  return km;
}

// km: largo en línea recta del orden propuesto. total: el mismo orden medido
// con `cost` (sin `cost`, igual a km).
export type Plan = { order: number[]; missing: number[]; km: number; total: number };

// Costo de ir de un nodo a otro: índices de `items`, o el inicio y el fin.
export type PlanNode = number | "start" | "end";
export type CostFn = (from: PlanNode, to: PlanNode) => number;

// Devuelve los índices de `items` en el orden sugerido: primero los que
// tienen coordenadas, después los que no (en `missing` también).
export function planOrder(items: (Partial<Pt> | null | undefined)[], start: Pt | null = null, end: Pt | null = null, cost?: CostFn): Plan {
  const located: number[] = [];
  const missing: number[] = [];
  items.forEach((p, i) => (hasCoords(p) ? located : missing).push(i));
  const pts = located.map((i) => items[i] as Pt);
  const n = pts.length;
  if (n === 0) return { order: missing, missing, km: 0, total: 0 };

  // Matriz de costos: nodos 0..n-1 las paradas, n el inicio, n+1 el fin.
  const S = n, E = n + 1;
  const nodes = [...pts, start, end];
  const nodeId = (k: number): PlanNode => (k === S ? "start" : k === E ? "end" : located[k]);
  const D: number[][] = nodes.map((a, i) => nodes.map((b, j) => (!a || !b || i === j ? 0 : cost ? cost(nodeId(i), nodeId(j)) : haversineKm(a, b))));
  // Sin inicio o sin fin, ese extremo no suma: el tramo vale 0.
  const d = (a: number, b: number) => D[a][b];

  // Vecino más cercano. Sin inicio, arranca por la parada más LEJANA al fin
  // si lo hay (así el recorrido se va acercando y termina cerca del fin), y
  // si no por la primera.
  const seq: number[] = [];
  const used = new Array(n).fill(false);
  let cur = start ? S : -1;
  if (cur === -1) {
    let first = 0;
    if (end) for (let i = 1; i < n; i++) if (d(i, E) > d(first, E)) first = i;
    seq.push(first); used[first] = true; cur = first;
  }
  while (seq.length < n) {
    let best = -1;
    for (let i = 0; i < n; i++) if (!used[i] && (best === -1 || d(cur, i) < d(cur, best))) best = i;
    seq.push(best); used[best] = true; cur = best;
  }

  const prev = (k: number) => (k === 0 ? (start ? S : -1) : seq[k - 1]);
  const next = (k: number) => (k === n - 1 ? (end ? E : -1) : seq[k + 1]);
  const dd = (a: number, b: number) => (a === -1 || b === -1 ? 0 : d(a, b));
  const EPS = 1e-9;

  for (let round = 0; round < 50; round++) {
    let improved = false;

    // 2-opt: dar vuelta el tramo seq[i..j]. inner = cuánto cambia recorrer
    // por dentro ese tramo al revés (0 en línea recta).
    for (let i = 0; i < n - 1; i++) {
      let inner = 0;
      for (let j = i + 1; j < n; j++) {
        inner += d(seq[j], seq[j - 1]) - d(seq[j - 1], seq[j]);
        const a = prev(i), b = seq[i], c = seq[j], e = next(j);
        const delta = dd(a, c) + dd(b, e) - dd(a, b) - dd(c, e) + inner;
        if (delta < -EPS) {
          for (let x = i, y = j; x < y; x++, y--) [seq[x], seq[y]] = [seq[y], seq[x]];
          improved = true;
          // El tramo ya está dado vuelta: lo acumulado se recalcula desde i.
          inner = 0;
          for (let x = i + 1; x <= j; x++) inner += d(seq[x], seq[x - 1]) - d(seq[x - 1], seq[x]);
        }
      }
    }

    // Or-opt: sacar un tramo de 1 a 3 paradas y ponerlo en otro lugar
    // (derecho o al revés).
    for (let len = 1; len <= 3; len++) {
      for (let i = 0; i + len <= n; i++) {
        const segFirst = seq[i], segLast = seq[i + len - 1];
        const a = prev(i), e = next(i + len - 1);
        const removeGain = dd(a, segFirst) + dd(segLast, e) - dd(a, e);
        let revInner = 0;
        for (let x = i + 1; x < i + len; x++) revInner += d(seq[x], seq[x - 1]) - d(seq[x - 1], seq[x]);
        const rest = [...seq.slice(0, i), ...seq.slice(i + len)];
        const restPrev = (k: number) => (k === 0 ? (start ? S : -1) : rest[k - 1]);
        let bestDelta = -EPS, bestPos = -1, bestRev = false;
        // Insertar antes de rest[k] (k = rest.length: al final).
        for (let k = 0; k <= rest.length; k++) {
          if (k === i) continue; // mismo lugar
          const p = restPrev(k), q = k === rest.length ? (end ? E : -1) : rest[k];
          const base = dd(p, q);
          const fwd = dd(p, segFirst) + dd(segLast, q) - base - removeGain;
          const rev = dd(p, segLast) + dd(segFirst, q) - base - removeGain + revInner;
          if (fwd < bestDelta) { bestDelta = fwd; bestPos = k; bestRev = false; }
          if (len > 1 && rev < bestDelta) { bestDelta = rev; bestPos = k; bestRev = true; }
        }
        if (bestPos !== -1) {
          const seg = seq.slice(i, i + len);
          if (bestRev) seg.reverse();
          const nuevo = [...rest.slice(0, bestPos), ...seg, ...rest.slice(bestPos)];
          for (let k = 0; k < n; k++) seq[k] = nuevo[k];
          improved = true;
        }
      }
    }
    if (!improved) break;
  }

  const order = seq.map((k) => located[k]);
  const km = pathKm(seq.map((k) => pts[k]), start, end);
  let total = km;
  if (cost) {
    const path = [...(start ? [S] : []), ...seq, ...(end ? [E] : [])];
    total = 0;
    for (let k = 1; k < path.length; k++) total += d(path[k - 1], path[k]);
  }
  return { order: [...order, ...missing], missing, km, total };
}

// Mueve el elemento de la posición `from` a la posición `to` (las demás corren).
export function moveTo<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return list;
  const n = [...list];
  const [x] = n.splice(from, 1);
  n.splice(Math.max(0, Math.min(to, n.length)), 0, x);
  return n;
}

// Turbo: el retiro en la tienda va antes que la entrega de sus envíos. Espejo
// de turboOrderError (server/current/routes/routeService.js), para no ofrecer
// un orden que el servidor va a rechazar. `stops` en el orden propuesto; una
// entrega cuya tienda no tiene retiro en el recorrido no se ata a ninguno.
export type TurboStop = { type: "PICKUP" | "DELIVERY"; store_id?: number | null; store_name?: string | null; delivery_store_ids?: (number | null | undefined)[] };
export function turboOrderError(stops: TurboStop[]): string | null {
  const withPickup = new Set(stops.filter((s) => s.type === "PICKUP" && s.store_id != null).map((s) => s.store_id as number));
  const picked = new Set<number>();
  for (let i = 0; i < stops.length; i++) {
    const s = stops[i];
    if (s.type === "PICKUP") { if (s.store_id != null) picked.add(s.store_id); continue; }
    for (const storeId of s.delivery_store_ids ?? []) {
      if (storeId != null && withPickup.has(storeId) && !picked.has(storeId)) {
        const pickup = stops.find((x) => x.type === "PICKUP" && x.store_id === storeId);
        return `En un Turbo el retiro en ${pickup?.store_name ?? `la tienda ${storeId}`} va antes que su entrega (parada ${i + 1}): ese orden no se guarda.`;
      }
    }
  }
  return null;
}
